/**
 * BuchhaltungRechnungen — Ausgangsrechnungen (sevdesk-Logik).
 *
 * Quelle: fibu_invoices (festgeschriebene Rechnungen, eigene Belege) +
 * Auftrags-Entwürfe (Lieferschein gedruckt, noch keine Rechnung).
 * „Rechnung erstellen" schreibt fest: Nummer + Buchung + offener Posten in
 * einer DB-Transaktion. Storno = Stornorechnung, nie Löschen (GoBD).
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Receipt, Search, Loader2, CheckCircle2, MoreHorizontal, ChevronLeft, ChevronRight,
  Download, Mail, Eye, Printer, Ban, FilePlus2, Send, Plus, FileCheck2, Trash2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/api/AuthContext';
import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';
import { downloadRechnung } from '@/components/orders/RechnungPDF';
import {
  ensureInvoice, openRechnungPreview, printRechnung, sendRechnungEmail,
} from '@/lib/rechnungVersand';
import { COMPANY_ID, fmtEUR } from '@/lib/buchhaltungApi';
import {
  listInvoices, listMigrationIssues, getInvoiceDetail, invoiceAsOrder, finalizeInvoice, deleteDraftInvoice,
} from '@/lib/invoiceApi';
import { buildRechnungRows, STATUS_TABS, tabStats } from '@/Components/rechnungen/rechnungRows';
import { PaymentDialog, StornoDialog } from '@/Components/rechnungen/RechnungDialogs';
import InvoiceDetailSheet from '@/Components/rechnungen/InvoiceDetailSheet';
import MigrationIssues from '@/Components/rechnungen/MigrationIssues';

const PAGE_SIZE = 25;

const CHANNELS = {
  shopify: { label: 'Shopify', class: 'bg-emerald-50 text-emerald-700' },
  ebay: { label: 'eBay', class: 'bg-amber-50 text-amber-700' },
  website: { label: 'Online', class: 'bg-blue-50 text-blue-700' },
  portal: { label: 'Portal', class: 'bg-blue-50 text-blue-700' },
  manuell: { label: 'Manuell', class: 'bg-violet-50 text-violet-700' },
  agent: { label: 'Agent', class: 'bg-slate-100 text-slate-600' },
};

const STATUS_BADGES = {
  entwurf: { label: 'Entwurf', class: 'bg-slate-100 text-slate-600' },
  offen: { label: 'Offen', class: 'bg-blue-50 text-blue-700' },
  faellig: { label: 'Fällig', class: 'bg-red-50 text-red-700' },
  bezahlt: { label: 'Bezahlt', class: 'bg-emerald-50 text-emerald-700' },
  teilbezahlt: { label: 'Teilbezahlt', class: 'bg-amber-50 text-amber-700' },
  storniert: { label: 'Storniert', class: 'bg-slate-100 text-slate-500' },
};

const date = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '—');

/** Auftrag mit den Daten der festgeschriebenen Rechnung (für PDF/Versand) */
const withInvoice = (row) => ({
  ...row.order,
  invoice_number: row.inv.invoice_no,
  invoiced_at: row.inv.invoice_date,
  _invoice: row.inv,
});

export default function BuchhaltungRechnungen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const actor = user?.email;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [page, setPage] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [paymentRow, setPaymentRow] = useState(null);
  const [stornoRow, setStornoRow] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list(),
  });
  const { data: invoices, isLoading: invLoading } = useQuery({ queryKey: ['fibu-invoices'], queryFn: listInvoices });
  const { data: openItems = [] } = useQuery({
    queryKey: ['fibu-open-items-all'],
    queryFn: async () => {
      const { data, error } = await SupabaseAdapter.getClient().from('fibu_open_items')
        .select('*').eq('company_id', COMPANY_ID).in('type', ['receivable', 'customer_credit']);
      if (error) throw error;
      return data || [];
    },
  });
  const { data: issues = [] } = useQuery({ queryKey: ['fibu-migration-issues'], queryFn: listMigrationIssues });

  const migrationMissing = invoices === null;
  const today = new Date().toISOString().slice(0, 10);
  const rows = useMemo(
    () => buildRechnungRows({ orders, invoices: invoices || [], openItems, today }),
    [orders, invoices, openItems, today],
  );
  const stats = useMemo(() => tabStats(rows), [rows]);
  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return rows
      .filter((r) => statusFilter === 'alle' || r.status === statusFilter)
      .filter((r) => !t || [r.customer, r.number, r.orderNumber].some((v) => (v || '').toLowerCase().includes(t)));
  }, [rows, statusFilter, searchTerm]);
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const guthaben = openItems.filter((i) => i.type === 'customer_credit' && Number(i.open_amount) > 0);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['fibu-invoices'] });
    queryClient.invalidateQueries({ queryKey: ['fibu-open-items-all'] });
    queryClient.invalidateQueries({ queryKey: ['fibu-migration-issues'] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['fibu-invoice'] });
  };

  const busy = async (key, fn) => {
    setBusyKey(key);
    try { await fn(); } catch (err) { toast.error(err.message); } finally { setBusyKey(null); }
  };

  // ── Aktionen ──
  const issue = (row) => busy(row.key, async () => {
    const finalized = await ensureInvoice(row.order, actor);
    toast.success(`Rechnung ${finalized.invoice_number} festgeschrieben und gebucht.`);
    refresh();
    return finalized;
  });
  const previewDraft = (row) => busy(row.key, () => openRechnungPreview(row.order));
  const pdfFor = async (row) => {
    if (row.order) return withInvoice(row);
    const detail = await getInvoiceDetail(row.inv.id);
    return invoiceAsOrder(detail, detail.lines);
  };
  const download = (row) => busy(row.key, async () => downloadRechnung(await pdfFor(row)));
  const print = (row) => busy(row.key, async () => printRechnung(await pdfFor(row)));
  const preview = (row) => busy(row.key, async () => openRechnungPreview(await pdfFor(row)));
  const sendOne = (row) => busy(row.key, async () => {
    if (!row.order) throw new Error('Manuelle Rechnungen bitte als PDF herunterladen und versenden.');
    const to = await sendRechnungEmail(row.inv ? withInvoice(row) : row.order, actor);
    toast.success(`Rechnung per E-Mail an ${to} gesendet.`);
    refresh();
  });
  const finalizeManual = (row) => busy(row.key, async () => {
    const inv = await finalizeInvoice(row.inv.id, actor);
    toast.success(`Rechnung ${inv.invoice_no} festgeschrieben und gebucht.`);
    refresh();
  });
  const discardDraft = (row) => busy(row.key, async () => {
    if (!window.confirm('Entwurf verwerfen?')) return;
    await deleteDraftInvoice(row.inv.id);
    toast.success('Entwurf verworfen.');
    refresh();
  });

  const draftOrders = filtered.filter((r) => r.kind === 'order');
  const allDraftsSelected = draftOrders.length > 0 && draftOrders.every((r) => selected.has(r.key));
  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const sendBulk = async () => {
    const targets = rows.filter((r) => selected.has(r.key) && r.kind === 'order');
    if (!targets.length) return;
    setBulkSending(true);
    let sent = 0;
    for (const r of targets) {
      try { await sendRechnungEmail(r.order, actor); sent++; } catch (err) { toast.warning(`${r.orderNumber}: ${err.message}`); }
    }
    setBulkSending(false);
    setSelected(new Set());
    refresh();
    if (sent) toast.success(`${sent} Rechnung(en) festgeschrieben und per E-Mail versendet.`);
  };

  const detailActions = {
    pdf: async (inv) => {
      const row = rows.find((r) => r.inv?.id === inv.id);
      if (row) download(row);
    },
    mail: (inv) => { const row = rows.find((r) => r.inv?.id === inv.id); if (row) sendOne(row); },
    gutschrift: (inv) => navigate(`/buchhaltung/gutschriften?invoice=${inv.id}`),
    storno: (inv) => { const row = rows.find((r) => r.inv?.id === inv.id); setDetailId(null); setStornoRow(row); },
  };

  return (
    <PageShell
      head={(
        <PageHeader
          icon={<Receipt className="w-5 h-5" />}
          title="Rechnungen"
          subtitle={`${rows.filter((r) => r.kind === 'invoice').length} Rechnungen · ${draftOrders.length} Entwürfe`}
          actions={(
            <Button onClick={() => navigate('/buchhaltung/rechnungen/neu')} className="h-11 gap-2">
              <Plus className="w-4 h-4" /><span className="sm:hidden">Neu</span><span className="hidden sm:inline">Neue Rechnung</span>
            </Button>
          )}
        />
      )}
    >
      {migrationMissing && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl p-3">
          Das neue Rechnungsmodul ist noch nicht eingerichtet. Bitte die Migrationen 20261016_01 bis _03 in Supabase ausführen.
        </p>
      )}
      <MigrationIssues issues={issues} actor={actor} onDone={refresh} />
      {guthaben.length > 0 && (
        <p className="text-sm text-slate-700 bg-white border border-slate-200 rounded-xl p-3">
          {guthaben.length} Kundenguthaben offen ({fmtEUR(guthaben.reduce((s, i) => s + Number(i.open_amount), 0))}) — Verrechnung/Erstattung erfolgt im Zahlungsabgleich.
        </p>
      )}

      <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Rechnungsstatus">
        {STATUS_TABS.map((t) => {
          const s = stats[t.key];
          const active = statusFilter === t.key;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={active}
              onClick={() => { setStatusFilter(t.key); setPage(0); }}
              className={cn('flex-shrink-0 min-h-[52px] px-3.5 rounded-xl border text-left transition-colors cursor-pointer',
                active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400')}>
              <span className="block text-[10px] font-bold uppercase leading-tight">
                {t.label} <span className={active ? 'text-white/60' : 'text-slate-400'}>({s.count})</span>
              </span>
              <span className={cn('block text-xs font-black tabular-nums', active ? 'text-white' : 'text-slate-900')}>{fmtEUR(s.sum)}</span>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Kunde, Rechnungs- oder Auftragsnummer…" className="pl-10 h-11 bg-white"
          value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }} />
      </div>

      {draftOrders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer min-h-[36px]">
            <input type="checkbox" className="w-4 h-4 accent-slate-900 cursor-pointer" checked={allDraftsSelected}
              onChange={() => setSelected(allDraftsSelected ? new Set() : new Set(draftOrders.map((r) => r.key)))} />
            Alle Entwürfe auswählen ({draftOrders.length})
          </label>
          <Button size="sm" className="h-9 ml-auto text-[11px] font-bold bg-slate-900 hover:bg-slate-800 gap-1.5"
            disabled={selected.size === 0 || bulkSending} onClick={sendBulk}>
            {bulkSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {selected.size} festschreiben &amp; per E-Mail versenden
          </Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[100px_100px_120px_1fr_90px_110px_110px_88px] gap-2 px-3 py-2.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
          <span>Status</span><span>Fälligkeit</span><span>Rechnungsnr.</span><span>Kunde</span><span>Datum</span>
          <span className="text-right">Betrag (netto)</span><span className="text-right">Offen (brutto)</span><span />
        </div>
        {(ordersLoading || invLoading) && (
          <p className="p-10 text-center text-sm text-slate-400"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Lade Rechnungen…</p>
        )}
        {!ordersLoading && !invLoading && pageRows.length === 0 && (
          <p className="p-10 text-center text-sm text-slate-400">Keine Einträge — Aufträge erscheinen hier als Entwurf, sobald der Lieferschein gedruckt wurde.</p>
        )}
        <div className="divide-y divide-slate-100">
          {pageRows.map((row) => {
            const badge = STATUS_BADGES[row.status];
            const isBusy = busyKey === row.key;
            const done = row.status === 'bezahlt' || row.status === 'storniert';
            const channel = CHANNELS[row.channel] || CHANNELS.agent;
            const isManualDraft = row.kind === 'invoice' && row.inv.status === 'draft';
            return (
              <div key={row.key} className="px-3 py-2.5 md:grid md:grid-cols-[100px_100px_120px_1fr_90px_110px_110px_88px] md:gap-2 md:items-center">
                <span className={cn('inline-flex w-fit text-[10px] font-bold px-2 py-0.5 rounded-full', badge.class)}>{badge.label}</span>
                <span className={cn('block text-xs text-slate-600 mt-1 md:mt-0', done && 'line-through text-slate-400')}>
                  {date(row.openItem?.due_date || row.inv?.due_date)}
                </span>
                <span className="block font-mono text-xs font-semibold text-slate-700 whitespace-nowrap truncate">
                  {row.number || <span className="text-slate-400 font-normal">{row.orderNumber || 'Entwurf'}</span>}
                </span>
                <button type="button" className="block min-w-0 text-left group"
                  onClick={() => (row.kind === 'invoice' && !isManualDraft ? setDetailId(row.inv.id) : row.kind === 'order' ? previewDraft(row) : navigate(`/buchhaltung/rechnungen/${row.inv.id}`))}>
                  <span className="block text-sm font-semibold text-slate-900 truncate group-hover:underline">{row.customer || 'Unbekannter Kunde'}</span>
                  <span className="flex items-center gap-1.5 mt-0.5">
                    <span className={cn('text-[9px] font-bold px-1.5 py-px rounded-full', channel.class)}>{channel.label}</span>
                    {row.inv?.sent_at && <span className="text-[9px] font-bold px-1.5 py-px rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" /> versendet</span>}
                  </span>
                </button>
                <span className="block text-xs text-slate-600">{date(row.date)}</span>
                <span className="block text-xs font-mono text-slate-700 md:text-right whitespace-nowrap tabular-nums">{fmtEUR(row.net)}</span>
                <span className={cn('block text-xs font-mono font-bold md:text-right whitespace-nowrap tabular-nums', row.open > 0 ? 'text-slate-900' : 'text-slate-400')}>{fmtEUR(row.open)}</span>
                <span className="flex items-center justify-end gap-1 mt-1.5 md:mt-0">
                  {row.kind === 'order' && (
                    <input type="checkbox" aria-label="Für Sammelversand auswählen" className="w-4 h-4 accent-slate-900 cursor-pointer"
                      checked={selected.has(row.key)} onChange={() => toggle(row.key)} />
                  )}
                  {isBusy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" aria-label="Aktionen" className="h-9 w-9 text-slate-500 hover:text-slate-900">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      {row.kind === 'order' && (
                        <>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => previewDraft(row)}><Eye className="w-4 h-4" /> Entwurf ansehen</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => issue(row)}><FileCheck2 className="w-4 h-4" /> Rechnung erstellen (festschreiben)</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => sendOne(row)}><Mail className="w-4 h-4" /> Erstellen &amp; per E-Mail</DropdownMenuItem>
                        </>
                      )}
                      {isManualDraft && (
                        <>
                          <DropdownMenuItem className="gap-2" onClick={() => navigate(`/buchhaltung/rechnungen/${row.inv.id}`)}><Pencil className="w-4 h-4" /> Bearbeiten</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => finalizeManual(row)}><FileCheck2 className="w-4 h-4" /> Festschreiben &amp; buchen</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2 text-red-600" disabled={isBusy} onClick={() => discardDraft(row)}><Trash2 className="w-4 h-4" /> Entwurf verwerfen</DropdownMenuItem>
                        </>
                      )}
                      {row.kind === 'invoice' && !isManualDraft && (
                        <>
                          <DropdownMenuItem className="gap-2" onClick={() => setDetailId(row.inv.id)}><Receipt className="w-4 h-4" /> Details &amp; Zahlungen</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => preview(row)}><Eye className="w-4 h-4" /> Vorschau</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => download(row)}><Download className="w-4 h-4" /> PDF herunterladen</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" disabled={isBusy} onClick={() => print(row)}><Printer className="w-4 h-4" /> Drucken</DropdownMenuItem>
                          {row.status !== 'storniert' && (
                            <>
                              <DropdownMenuItem className="gap-2" disabled={isBusy || !row.order} onClick={() => sendOne(row)}>
                                <Mail className="w-4 h-4" /> Per E-Mail versenden
                                {row.inv.sent_at && <CheckCircle2 className="w-3 h-3 ml-auto text-emerald-600" />}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {row.openItem && Number(row.openItem.open_amount) > 0 && (
                                <DropdownMenuItem className="gap-2 text-emerald-700" onClick={() => setPaymentRow(row)}><CheckCircle2 className="w-4 h-4" /> Zahlung buchen</DropdownMenuItem>
                              )}
                              <DropdownMenuItem className="gap-2 text-amber-700" onClick={() => navigate(`/buchhaltung/gutschriften?invoice=${row.inv.id}`)}><FilePlus2 className="w-4 h-4" /> Gutschrift erstellen</DropdownMenuItem>
                              <DropdownMenuItem className="gap-2 text-red-600" onClick={() => setStornoRow(row)}><Ban className="w-4 h-4" /> Stornieren (Stornorechnung)</DropdownMenuItem>
                            </>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </div>
            );
          })}
        </div>
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 py-3 border-t bg-slate-50/50">
            <Button size="icon" variant="ghost" aria-label="Vorherige Seite" className="h-9 w-9" disabled={page === 0} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-xs text-slate-600 tabular-nums">{page * PAGE_SIZE + 1} – {Math.min((page + 1) * PAGE_SIZE, filtered.length)} von {filtered.length}</span>
            <Button size="icon" variant="ghost" aria-label="Nächste Seite" className="h-9 w-9" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        )}
      </Card>

      {paymentRow && <PaymentDialog row={paymentRow} open={!!paymentRow} onOpenChange={(v) => !v && setPaymentRow(null)} actor={actor} onDone={refresh} />}
      {stornoRow && <StornoDialog row={stornoRow} open={!!stornoRow} onOpenChange={(v) => !v && setStornoRow(null)} actor={actor} onDone={refresh} />}
      <InvoiceDetailSheet invoiceId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} actions={detailActions} />
    </PageShell>
  );
}
